import {
    SectionCard,
    SectionCardH1,
    SectionCardHeading,
    SectionCardSubH1,
} from '../SectionCard';
import { tooltips, views } from '@/copy';
import { useGoogleData, useTopKeywords } from 'utils/hooks';
import useShowTranslations, {
    translationCSVColumn,
} from 'utils/hooks/useShowTranslations';
import DefinitionTooltip from '../DefinitionTooltip';
import TopPhrasesSection from './TopPhrasesSection';
import { downloadItemsAsCSV } from 'utils/formatCSV';
import sortDataByKey from 'utils/sortDataByKey';

const defaultCSVColumns = [
    { key: 'kw', label: 'search query' },
    { key: 'volume' },
];

function TopKeywordsSectionHeading({ name, children, ...props }) {
    return (
        <SectionCardHeading {...props}>
            <SectionCardH1>Top Search Queries</SectionCardH1>
            <SectionCardSubH1>
                for <span className="capitalize">{name}</span> by Volume{' '}
                <span className="capitalize">{views.descending}</span>
            </SectionCardSubH1>
            &nbsp;
            <DefinitionTooltip
                {...tooltips.searchQueries}
                mixpanelMeta={{ contentKey: 'searchQueries' }}
                openOnClick={true}
                width="25rem"
            />
            {children}
        </SectionCardHeading>
    );
}

function NoData() {
    return (
        <div
            css={`
                color: ${({ theme }) => theme.neutral_3};
            `}
        >
            No search queries to show.
        </div>
    );
}

function TopKeywordsSection({
    enabled,
    data,
    paginateBy,
    controller,
    filename,
    WrapperComponent = SectionCard,
    HeadingComponent = TopKeywordsSectionHeading,
    NoDataComponent = NoData,
    ...props
}) {
    const { topKeywords, ...topKeywordsQuery } = useTopKeywords({
        enabled,
        data,
        paginateBy,
        controller,
    });
    const queryData = {
        keywords: topKeywords,
        ...data,
    };
    const kwQuery = useGoogleData({
        enabled: !!(topKeywords && enabled),
        data: queryData,
        paginateBy: 'keywords',
        controller,
    });
    const isLoading = [topKeywordsQuery.status, kwQuery.status].includes(
        'loading',
    );
    const sortedData =
        kwQuery.data && kwQuery.data.length
            ? sortDataByKey({
                  ascending: false,
                  data: kwQuery.data,
                  key: 'volume',
              }).map((kw) => ({ ...kw, flagId: kw.id }))
            : null;
    const dataReady =
        sortedData && !kwQuery.isPlaceholderData && kwQuery.isSuccess;
    const showTranslations = useShowTranslations({
        rows: sortedData,
        countries: queryData.countries,
    });
    const handleDownload = () => {
        const columns = showTranslations
            ? [...defaultCSVColumns, translationCSVColumn]
            : defaultCSVColumns;

        downloadItemsAsCSV(filename, sortedData, columns);
    };

    const mixpanelMeta = { contentKey: 'TopKeywords' };

    return (
        <TopPhrasesSection
            data={data}
            isLoading={isLoading}
            sortedData={sortedData}
            dataReady={dataReady}
            showTranslations={showTranslations}
            handleDownload={handleDownload}
            mixpanelMeta={mixpanelMeta}
            WrapperComponent={WrapperComponent}
            HeadingComponent={HeadingComponent}
            NoDataComponent={NoDataComponent}
            {...props}
        />
    );
}

export default TopKeywordsSection;
